import { V1VendorFeedSpecs } from 'new/components/flows/types/elt/CreateELTFlow/types';
import {
  CapabilityChecker,
  extractV1Capabilities,
  extractV1AdditionalParams,
} from '../constants/capabilities';
import { getDebugLogger } from 'new/hooks/useDebugLogger';

// Debug logging is now controlled by the UI_ENABLE_DEBUGGER feature flag
const debugLogger = getDebugLogger();

interface StrategyMapping {
  [strategyName: string]: string[]; // strategy -> ref_feeds array
}

/**
 * V1 Template Processor
 *
 * This processor handles V1 ELT templates and maintains backward compatibility
 * with existing UI components while adding the new unified capabilities system.
 *
 * Key Functions:
 * - Processes V1 resource_parameters into unified capabilities structure
 * - Maintains original ref_feed_options structure for UI compatibility
 * - Extracts additional_params (non-capability parameters)
 * - Builds strategy mappings for future V2 compatibility
 *
 * Input: vendor, availableFeeds, discoveryResults, vendorEndpoints, vendorSpec
 * Output: vendorFeedSpecs with unified capabilities + original structure
 */
export function processV1Template(
  vendor: any,
  availableFeeds: any[],
  discoveryResults: any,
  vendorEndpoints: any, // Add vendor endpoints to extract resource_parameters,
  vendorSpec: any, //sourceConfigById
): { vendorFeedSpecs: V1VendorFeedSpecs; strategyMappings: any; feedBasedOptions: any } {
  const refFeedOptions = {
    display: {},
    feed_names: {},
    ref_field_list_selector: '',
    ref_fields_names: '',
    default_ref_feed: null,
    default_include_feeds: [],
    supports_force_resync: {},
    object_detection_mode_selector_param: undefined,
    single_schema_param: undefined,
  };

  // Build feedBasedOptions exactly like the old code
  const feedBasedOptions = { feed_types: {}, primary_keys: {} };
  const refFeedNames: string[] = [];
  let defaultRefFeed: string | null = null;

  // Process V1 parameters using the EXACT same logic as old code
  Object.values(vendorSpec).forEach((config: any) => {
    const {
      name,
      is_base_feed_param = false,
      is_common_param = false,
      param_type = 'none',
      ...otherParamProps
    } = config;

    switch (param_type) {
      case 'feed_types': {
        // This block is for knowing all available base feed types for this vendor
        defaultRefFeed = otherParamProps?.default;

        otherParamProps.options?.forEach((v: any) => {
          refFeedOptions.display[v.value] = {
            label: v.label,
            value: v.value,
          };
          refFeedNames.push(v.value);
        });

        if (!refFeedOptions.display[otherParamProps?.default]) {
          refFeedOptions.display[otherParamProps?.default] = {
            label: otherParamProps?.default,
            value: otherParamProps?.default,
          };
          refFeedNames.push(otherParamProps?.default);
        }
        break;
      }

      case 'feed_name': {
        // This block is for knowing what how to map the feed name to the base feeds
        const feedName = otherParamProps?.ref_spec_name || name.split('.')[2];
        const param = otherParamProps?.default?.replace(/[{}]/g, '');

        refFeedOptions.feed_names[feedName] = {
          param: param,
        };
        break;
      }

      case 'field_list_selector': {
        const param = otherParamProps?.default?.replace(/[{}]/g, '');
        refFeedOptions.ref_field_list_selector = param;
        break;
      }

      case 'field_list': {
        const param = otherParamProps?.default?.replace(/[{}]/g, '');
        refFeedOptions.ref_fields_names = param;
        break;
      }

      case 'primary_key_selectors': {
        // This block is for knowing the primary key for each feed
        otherParamProps.values?.forEach((v: string) => {
          let primaryKey = otherParamProps?.default || '';

          // Handle computed primary key if spec_processing_rules exists
          if (otherParamProps?.spec_processing_rules?.primary_key) {
            const { type, rule } = otherParamProps.spec_processing_rules.primary_key;

            if (type === 'computed' && rule.operation === 'concat') {
              // Handle concatenation operation
              primaryKey = rule.parts
                .map((part: any) => {
                  if (part?.type === 'variable') {
                    return part?.value;
                  } else if (part.type === 'literal') {
                    // For literal type, use the value directly
                    return part.value;
                  }
                  return '';
                })
                .join('');
            }
          }

          feedBasedOptions.primary_keys[v] = {
            primary_key: primaryKey,
          };
        });
        break;
      }

      case 'base_feed_selector': {
        // This block is for knowing which base feed types are available for each feed
        const feedName = otherParamProps?.base_feed_name || name.split('.')[2];
        const feedSelectionMode = otherParamProps?.feed_selection_mode || 'include';

        if (feedSelectionMode === 'exclude') {
          // This feed should be included for everything except the feeds in the exclude list.
          refFeedOptions.default_include_feeds.push(feedName);
        }

        otherParamProps.values?.forEach((v: string) => {
          // Also note that v= [nx_all] or [] will result in including always. doesn't make sense so acts same as include with [nx_all]

          feedBasedOptions.feed_types[v] =
            otherParamProps?.force_selector === true
              ? { forceFeed: feedName }
              : {
                  [feedName]: feedSelectionMode === 'exclude' ? false : true,
                };

          // Determine if this base feed type supports force_resync
          // For now, base_auto supports it by default
          if (v === 'base_auto') {
            refFeedOptions.supports_force_resync[v] = true;
          }
        });
        break;
      }

      case 'object_detection_mode_selector': {
        // Store the parameter name and current value for easy access
        refFeedOptions.object_detection_mode_selector_param = name;
        break;
      }

      case 'single_multi_schema_selector': {
        // Store the parameter name and current value for easy access
        refFeedOptions.single_schema_param = name;
        break;
      }

      case 'none':
      default:
        break;
    }
  });

  // Set defaults exactly like old code
  if (defaultRefFeed) {
    refFeedOptions.default_include_feeds.push(defaultRefFeed);
    refFeedOptions.default_ref_feed = defaultRefFeed;
  }

  // Create strategy mappings for future use (but don't change the main structure)
  const strategyMappings: StrategyMapping = {};
  refFeedNames.forEach((refSpec, index) => {
    strategyMappings[refSpec] = [refSpec];
  });

  // Extract resource_parameters from vendor endpoints for V1 capabilities
  // If processedEndpoints.feeds exists and is an array, use their resource_parameters
  const feedsEndpoint = vendorEndpoints?.feeds[0] || {};
  const resourceParams = feedsEndpoint?.resource_parameters || [];

  debugLogger.debugLog(2, 'vendorEndpoints', vendorEndpoints);
  debugLogger.debugLog(2, 'resourceParams', resourceParams);
  debugLogger.debugLog(2, 'capabilities', extractV1Capabilities(resourceParams));
  debugLogger.debugLog(2, 'additional_params', extractV1AdditionalParams(resourceParams));

  return {
    vendorFeedSpecs: {
      ref_feed_options: refFeedOptions,
      data_feed_options: {}, // Keep empty as old code builds this from availableFeeds
      // V1 capabilities extracted from resource_params with embedded params
      capabilities: extractV1Capabilities(resourceParams),
      // Additional parameters that are not part of capability decision-making
      additional_params: extractV1AdditionalParams(resourceParams),
    },
    strategyMappings,
    feedBasedOptions, // Return this to match old code structure
  };
}
